// src/components/SessionLogForm.tsx

import React, { FormEvent, useEffect, useState } from "react";
import {
  X,
  UserRound,
  BookOpen,
  MapPin,
  CalendarDays,
  Clock3,
  FileText,
} from "lucide-react";
import { CreateSessionLogRequest } from "../types/sessionLog";
import { getCoursesByTeacher } from "../api/courses";
import { getTeachers } from "../api/teachers";
import { Course } from "../types/course";
import { Teacher } from "../types/teacher";

interface Props {
  initial?: Partial<CreateSessionLogRequest> & {
    id?: string;
    teacherName?: string;
  };
  onSubmit: (data: CreateSessionLogRequest, id?: string) => Promise<void>;
  onCancel: () => void;
}

export default function SessionLogForm({
  initial = {},
  onSubmit,
  onCancel,
}: Props) {
  const [courses, setCourses] = useState<Course[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [form, setForm] = useState<CreateSessionLogRequest>({
    teacher: initial.teacher || "",
    course: initial.course || "",
    location: initial.location || "",
    date: initial.date || new Date().toISOString().slice(0, 10),
    startTime: initial.startTime || "09:00",
    endTime: initial.endTime || "10:00",
    topicCovered: initial.topicCovered || "",
    notes: initial.notes || "",
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Load all teachers on mount
  useEffect(() => {
    getTeachers().then(setTeachers);
  }, []);

  // Whenever the selected teacher changes, fetch only their active courses
  useEffect(() => {
    if (form.teacher) {
      getCoursesByTeacher(form.teacher)
        .then(setCourses)
        .catch(() => setCourses([]));
    } else {
      setCourses([]);
    }
  }, [form.teacher]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "teacher" ? { course: "" } : {}),
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      await onSubmit(form, initial.id);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const isEdit = Boolean(initial.id);

  const fieldClass = `
    box-border h-11 w-full min-w-0 rounded-[10px]
    border border-slate-200 bg-white px-3.5
    text-[13px] font-medium text-slate-800
    outline-none transition
    placeholder:text-slate-400
    hover:border-slate-300
    focus:border-emerald-400
    focus:ring-2 focus:ring-emerald-100
    disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400
  `;

  const labelClass = `
    mb-1.5 block text-[10px] font-semibold uppercase
    tracking-[0.05em] text-slate-500
  `;

  const iconClass =
    "pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400";

  return (
    <div
      className="
        fixed inset-0 z-[1600]
        flex items-center justify-center
        bg-slate-950/45 px-4 py-6
        backdrop-blur-[2px]
      "
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !submitting) {
          onCancel();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="session-log-form-title"
        className="
          flex max-h-[92vh] w-full max-w-[760px]
          flex-col overflow-hidden
          rounded-[18px]
          border border-slate-200
          bg-white
          shadow-[0_24px_70px_rgba(15,23,42,0.22)]
        "
      >
        {/* Header */}
        <div
          className="
            flex shrink-0 items-start justify-between gap-4
            border-b border-slate-100
            px-6 py-5
            max-sm:px-4
          "
        >
          <div className="min-w-0">
            <p
              className="
                m-0 text-[10px] font-bold uppercase
                tracking-[0.12em] text-blue-600
              "
            >
              Session management
            </p>

            <h2
              id="session-log-form-title"
              className="
                mb-0 mt-1 text-[20px]
                font-bold tracking-[-0.02em]
                text-slate-950
              "
            >
              {isEdit ? "Edit Session Log" : "New Session Log"}
            </h2>

            <p className="mb-0 mt-1 text-[12px] leading-5 text-slate-500">
              {isEdit
                ? "Update the session details below."
                : "Record a new teaching session."}
            </p>
          </div>

          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            aria-label="Close session form"
            title="Close"
            className="
              inline-flex h-9 w-9 shrink-0 items-center justify-center
              rounded-[9px]
              border border-emerald-600
              bg-emerald-600
              text-white transition
              hover:border-emerald-700 hover:bg-emerald-700
              focus:outline-none focus:ring-2 focus:ring-emerald-200
              disabled:cursor-not-allowed disabled:opacity-50
            "
          >
            <X size={17} />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-6 mt-4 rounded-[10px] border border-red-200 bg-red-50 px-4 py-3 text-[12px] font-medium text-red-700 max-sm:mx-4">
            {error}
          </div>
        )}

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="min-h-0 flex-1 overflow-y-auto"
        >
          <div className="grid grid-cols-1 gap-x-4 gap-y-4 px-6 py-5 sm:grid-cols-2 max-sm:px-4">
            {/* Teacher */}
            {isEdit ? (
              <div className="sm:col-span-2">
                <span className={labelClass}>Teacher</span>

                <div
                  className="
                    flex min-h-11 items-center gap-2.5
                    rounded-[10px]
                    border border-slate-200
                    bg-slate-50 px-3.5
                    text-[13px] font-semibold text-slate-700
                  "
                >
                  <UserRound size={15} className="shrink-0 text-slate-400" />
                  <span className="truncate">
                    {initial.teacherName || "—"}
                  </span>
                </div>
              </div>
            ) : (
              <label className="min-w-0">
                <span className={labelClass}>Teacher</span>

                <div className="relative">
                  <UserRound size={15} className={iconClass} />

                  <select
                    id="teacher"
                    name="teacher"
                    value={form.teacher}
                    onChange={handleChange}
                    required
                    className={`${fieldClass} pl-10 pr-8`}
                  >
                    <option value="" disabled>
                      Select teacher
                    </option>

                    {teachers.map((teacher) => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.name} ({teacher.email})
                      </option>
                    ))}
                  </select>
                </div>
              </label>
            )}

            {/* Course */}
            <label className="min-w-0">
              <span className={labelClass}>Course</span>

              <div className="relative">
                <BookOpen size={15} className={iconClass} />

                <select
                  id="course"
                  name="course"
                  value={form.course}
                  onChange={handleChange}
                  required
                  disabled={!form.teacher}
                  className={`${fieldClass} pl-10 pr-8`}
                >
                  <option value="" disabled>
                    {form.teacher ? "Select course" : "Select teacher first"}
                  </option>

                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.name}
                    </option>
                  ))}
                </select>
              </div>
            </label>

            {/* Location */}
            <label className="min-w-0">
              <span className={labelClass}>Location</span>

              <div className="relative">
                <MapPin size={15} className={iconClass} />

                <input
                  id="location"
                  name="location"
                  type="text"
                  value={form.location}
                  onChange={handleChange}
                  required
                  placeholder="Enter location"
                  className={`${fieldClass} pl-10`}
                />
              </div>
            </label>

            {/* Date */}
            <label className="min-w-0">
              <span className={labelClass}>Date</span>

              <div className="relative">
                <CalendarDays size={15} className={iconClass} />

                <input
                  id="date"
                  name="date"
                  type="date"
                  value={form.date}
                  onChange={handleChange}
                  required
                  className={`${fieldClass} pl-10`}
                />
              </div>
            </label>

            {/* Start */}
            <label className="min-w-0">
              <span className={labelClass}>Start Time</span>

              <div className="relative">
                <Clock3 size={15} className={iconClass} />

                <input
                  id="startTime"
                  name="startTime"
                  type="time"
                  value={form.startTime}
                  onChange={handleChange}
                  required
                  className={`${fieldClass} pl-10`}
                />
              </div>
            </label>

            {/* End */}
            <label className="min-w-0">
              <span className={labelClass}>End Time</span>

              <div className="relative">
                <Clock3 size={15} className={iconClass} />

                <input
                  id="endTime"
                  name="endTime"
                  type="time"
                  value={form.endTime}
                  onChange={handleChange}
                  required
                  className={`${fieldClass} pl-10`}
                />
              </div>
            </label>

            {/* Topic */}
            <label className="min-w-0 sm:col-span-2">
              <span className={labelClass}>Topic Covered</span>

              <div className="relative">
                <BookOpen size={15} className={iconClass} />

                <input
                  id="topicCovered"
                  name="topicCovered"
                  type="text"
                  value={form.topicCovered}
                  onChange={handleChange}
                  required
                  placeholder="Enter topic covered"
                  className={`${fieldClass} pl-10`}
                />
              </div>
            </label>

            {/* Notes */}
            <label className="min-w-0 sm:col-span-2">
              <span className={labelClass}>Notes</span>

              <div className="relative">
                <FileText size={15} className={iconClass} />

                <input
                  id="notes"
                  name="notes"
                  type="text"
                  value={form.notes}
                  onChange={handleChange}
                  placeholder="Add optional notes"
                  className={`${fieldClass} pl-10`}
                />
              </div>
            </label>
          </div>

          {/* Footer */}
          <div
            className="
              flex shrink-0 items-center justify-end gap-2
              border-t border-slate-100
              bg-slate-50/50
              px-6 py-4
              max-sm:px-4
            "
          >
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="
                inline-flex h-10 items-center justify-center
                rounded-[9px]
                border border-emerald-200
                bg-white px-4
                text-[12px] font-semibold text-emerald-700
                transition
                hover:border-emerald-300 hover:bg-emerald-50
                hover:text-emerald-800
                focus:outline-none focus:ring-2 focus:ring-emerald-200
                disabled:cursor-not-allowed disabled:opacity-50
              "
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={submitting}
              className="
                inline-flex h-10 min-w-[108px] items-center justify-center
                gap-2 rounded-[9px]
                border border-emerald-600
                bg-emerald-600 px-4
                text-[12px] font-semibold text-white
                transition
                hover:border-emerald-700 hover:bg-emerald-700
                focus:outline-none focus:ring-2 focus:ring-emerald-200
                disabled:cursor-not-allowed disabled:opacity-60
              "
            >
              {submitting && (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
              )}

              {submitting
                ? isEdit
                  ? "Saving..."
                  : "Creating..."
                : isEdit
                  ? "Save"
                  : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
